/**
 * Floorplan Demo Page
 * 
 * Demonstrates the expandable floorplan section with controls
 */

import React, { useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { 
  ThemeProvider, 
  CssBaseline, 
  Box, 
  Typography, 
  Paper,
  Alert,
  Chip,
} from '@mui/material';
import { configureStore } from '@reduxjs/toolkit';
import { lightTheme } from '../../theme';
import MuiNavContainer from '../organisms/NavContainer';
import ToolContext from '../services/ToolContext';

// Mock Redux store for demo
const mockStore = configureStore({
  reducer: {
    ui: (state = {
      showing: 'room',
      currentView: 'floorplan',
      exitToRoom: false,
    }) => state,
    container: (state = {
      project: { name: 'Conference Room Design' },
      activeTab: 'room1',
      areas: {},
    }) => state,
  },
});

// Mock tool context with floorplan
const mockTool = {
  menus: {
    floorplan: { 
      room: {
        name: 'floorplan',
        label: 'Floor Plan',
        expanded: true,
      }
    },
    furniture: { room: true },
    walls: { room: true },
    devices: { room: true },
    coverage: { room: true },
    analysis: { room: true },
  },
  constants: {
    tool: {
      fullName: 'Room Designer',
      name: 'room-designer',
      path: { long: 'room-designer' },
    },
  },
};

const FloorplanDemo: React.FC = () => {
  const [roomDimensions, setRoomDimensions] = useState({ width: 20, length: 14, height: 9.5 });
  const [unit, setUnit] = useState<'feet' | 'meters'>('feet');

  return (
    <Provider store={mockStore}>
      <BrowserRouter>
        <ToolContext.Provider value={mockTool as any}>
          <ThemeProvider theme={lightTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', height: '100vh' }}>
              {/* NavContainer with expandable floorplan */}
              <MuiNavContainer />
              
              {/* Main content area */}
              <Box sx={{ flexGrow: 1, p: 3, backgroundColor: '#f5f5f5', overflow: 'auto' }}>
                <Typography variant="h4" gutterBottom>
                  Floorplan Controls Demo
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  Click on "FLOORPLAN" in the navigation to expand and see the room configuration controls.
                </Alert>

                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Current Room Configuration
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Width: ${roomDimensions.width} ${unit}`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip 
                      label={`Length: ${roomDimensions.length} ${unit}`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip 
                      label={`Height: ${roomDimensions.height} ${unit}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Paper>

                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Floorplan Controls Features
                  </Typography>
                  <ul>
                    <li><strong>Room Name:</strong> Editable text field for naming the room</li>
                    <li><strong>Room Dimensions:</strong> Width and length with increment/decrement buttons</li>
                    <li><strong>Ceiling Height:</strong> Adjustable height with 0.5 ft increments</li>
                    <li><strong>Ceiling Tile Dimension:</strong> Dropdown with standard tile sizes</li>
                    <li><strong>Unit Toggle:</strong> Switch between feet and meters</li>
                    <li><strong>Add Table Button:</strong> Continue to furniture placement</li>
                  </ul>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Navigation Sections
                  </Typography>
                  <Typography variant="body2" paragraph>
                    The navigation sidebar now includes expandable sections:
                  </Typography>
                  <ul>
                    <li><strong>FLOORPLAN:</strong> Room configuration and dimensions</li>
                    <li><strong>FURNITURE:</strong> Table and seating arrangements</li>
                    <li><strong>WALLS:</strong> Wall and door placement</li>
                    <li><strong>DEVICES:</strong> Audio/video equipment placement</li>
                    <li><strong>COVERAGE:</strong> Coverage visualization settings</li>
                    <li><strong>ANALYSIS:</strong> Performance analysis tools</li>
                  </ul>
                </Paper>
              </Box>
            </Box>
          </ThemeProvider>
        </ToolContext.Provider>
      </BrowserRouter>
    </Provider>
  );
};

export default FloorplanDemo;